import type { Api } from "../api";
import { empty, h, label, pill } from "../dom";
import { qty, unitPrice } from "../format";
import { navigate } from "../nav";
import { words } from "../orders";
import { dims, effective, pricesBySku, searchArgs } from "../products";
import type { Me, Page, Product, SkuPrices } from "../types";
import { can } from "./shared";

// Products (products, products.read): a customer's products with their SKUs, sizes, and the requirements in force
// (required BCT, safety factor, stacking, pallet layers) with the board options that meet them. With costs.read the
// prices come too (prices): Tare's price to the customer by market and break, the cost and GM, and the suppliers'
// agreed delivered prices. Internal: none of it goes to a customer as it stands. Read only.

export async function products(api: Api, me: Me, query: URLSearchParams): Promise<HTMLElement> {
  const customer = (query.get("customer") ?? "").slice(0, 100);
  const q = (query.get("q") ?? "").slice(0, 100);
  const cursor = /^[0-9]{1,6}$/.test(query.get("cursor") ?? "") ? query.get("cursor")! : undefined;
  const search = searchArgs(q);
  const withPrices = can(me, "costs.read");
  const asked = customer || q;
  const [page, prices] = asked
    ? await Promise.all([
        api.tool<Page<Product>>("products", { limit: 20, ...(customer ? { customer } : {}), ...search, ...(cursor ? { cursor } : {}) }),
        withPrices
          ? api.tool<Page<SkuPrices>>("prices", { limit: 200, ...(customer ? { customer } : {}), ...("sku" in search ? { sku: search.sku } : {}) })
          : Promise.resolve(null),
      ])
    : [null, null];
  const bySku = pricesBySku(prices?.rows ?? []);

  const customerInput = h("input", { id: "customer", type: "search", value: customer, placeholder: "LTMW, Creative…", maxlength: 100 });
  const qInput = h("input", { id: "q", type: "search", value: q, placeholder: "BOX0048, or TG-CRTV-…", maxlength: 100 });
  const form = h("form", { class: "filters", role: "search" },
    h("div", { class: "field" }, h("label", { for: "customer" }, "Customer"), customerInput),
    h("div", { class: "field" }, h("label", { for: "q" }, "Part number or SKU"), qInput),
    h("button", { type: "submit", class: "button" }, "Find"));
  form.addEventListener("submit", (e) => {
    e.preventDefault();
    navigate(`/products?${new URLSearchParams({ ...(customerInput.value.trim() ? { customer: customerInput.value.trim() } : {}),
      ...(qInput.value.trim() ? { q: qInput.value.trim() } : {}) })}`);
  });

  const skuPrices = (sku: string) => {
    const p = bySku.get(sku);
    if (!withPrices) return null;
    if (!p || (!p.customerPrices.length && !p.supplierPrices.length)) return h("p", { class: "caption" }, "No prices for this SKU.");
    return h("div", { class: "scroll" }, h("table", { class: "tbl" },
      h("thead", null, h("tr", null, h("th", { scope: "col" }, "Price to the customer"), h("th", { scope: "col" }, "Market"),
        h("th", { scope: "col", class: "r" }, "From EA"), h("th", { scope: "col", class: "r" }, "Price / EA"),
        h("th", { scope: "col", class: "r" }, "Cost / EA"), h("th", { scope: "col", class: "r" }, "GM"))),
      h("tbody", null, p.customerPrices.map((c) => h("tr", null,
        h("td", null, c.priceList ?? words(c.break)), h("td", null, c.market ?? c.shipTo ?? "Any"),
        h("td", { class: "num r" }, qty(c.minQtyEa, "")), h("td", { class: "num r strong" }, unitPrice(c.unitPrice)),
        h("td", { class: "num r" }, unitPrice(c.costPerEa)),
        h("td", { class: "num r" }, c.gmPct === null ? "" : `${c.gmPct.toFixed(1)}%`))),
        p.supplierPrices.map((s) => h("tr", null,
          h("td", null, `${s.supplier?.name ?? "Supplier"}, ${s.status.toLowerCase()}`, s.source ? h("div", { class: "caption" }, s.source) : null),
          h("td", null, [s.market, s.plant].filter(Boolean).join(" · ") || "Any"),
          h("td", { class: "num r" }, effective(s.effective)), h("td", { class: "num r" }, ""),
          h("td", { class: "num r" }, unitPrice(s.deliveredPerEa)), h("td", null, ""))))));
  };

  const card = (p: Product) => h("section", { class: "panel decision", "aria-label": p.partNumber },
    h("div", { class: "row" },
      h("div", null,
        h("h2", { class: "subheading" }, h("span", { class: "num" }, p.partNumber), p.description ? ` · ${p.description}` : ""),
        h("p", { class: "caption" }, [p.customer?.name, [p.form, p.style].filter(Boolean).join(" "), p.insideIn ? `inside ${dims(p.insideIn)}` : null,
          p.structureRevision].filter(Boolean).join(" · "))),
      pill(p.status.toLowerCase(), p.status === "ACTIVE" ? "good" : "neutral")),
    p.requirements.map((r) => h("div", { class: "stack-tight" },
      label(`${words(r.season)}${r.revision ? `, ${r.revision}` : ""}`),
      h("p", { class: "caption" }, [r.requiredBctLb ? `BCT required ${r.requiredBctLb} lb` : null, r.sfTarget ? `safety factor ${r.sfTarget}` : null,
        r.stacking, r.palletLayers ? `${r.palletLayers} layers` : null, r.conditioning].filter(Boolean).join(" · ")),
      r.options.length
        ? h("div", { class: "scroll" }, h("table", { class: "tbl" },
            h("thead", null, h("tr", null, h("th", { scope: "col" }, "Option"), h("th", { scope: "col" }, "Box board"),
              h("th", { scope: "col" }, "Insert board"), h("th", { scope: "col", class: "r" }, "Est. BCT"), h("th", { scope: "col", class: "r" }, "Est. SF"))),
            h("tbody", null, r.options.map((o) => h("tr", null,
              h("td", null, o.option === "-" ? "Standard" : o.option, o.supplierAlternate ? h("div", { class: "caption" }, "supplier's alternate") : null),
              h("td", { class: "num" }, o.boxBoard ?? ""), h("td", { class: "num" }, o.insertBoard ?? ""),
              h("td", { class: "num r" }, o.estCombinedBctLb ? `${o.estCombinedBctLb} lb` : ""),
              h("td", { class: "num r" }, o.estSf === null ? "" : o.estSf.toFixed(2)))))))
        : null)),
    p.skus.map((s) => h("div", { class: "stack-tight" },
      h("div", { class: "row-act" }, h("span", { class: "num strong" }, s.sku), s.description ? h("span", { class: "caption" }, s.description) : null,
        pill(s.status.toLowerCase(), s.status === "ACTIVE" ? "good" : "neutral")),
      skuPrices(s.sku))));

  return h("div", { class: "page" },
    h("div", { class: "stack-tight" },
      h("h1", { class: "title" }, "Products"),
      h("p", { class: "lede" }, page
        ? page.rows.length ? `${page.rows.length}${page.next_cursor ? "+" : ""} ${page.rows.length === 1 ? "product" : "products"}.` : "No products match."
        : "Find a customer's products by customer, part number or SKU."),
      withPrices ? h("p", { class: "caption" }, "Internal: cost, margin and supplier prices never go to a customer.") : null),
    form,
    page ? (page.rows.length ? page.rows.map(card) : empty("Nothing here", "Try part of the number, or another customer.")) : null,
    page?.next_cursor
      ? h("div", null, h("a", { class: "button", href: `/products?${new URLSearchParams({ ...(customer ? { customer } : {}), ...(q ? { q } : {}),
          cursor: page.next_cursor })}` }, "Next 20"))
      : null);
}
